import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, serviceSchema, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Hur skriver man ett manus till reklamfilm?",
    a: "Börja i insikten: varför ska någon bry sig. Formulera en hook som fungerar inom tre sekunder, ett enda budskap, en demonstration av påståendet och en tydlig avslutning med varumärke och uppmaning. Skriv sedan om filmen i 30-, 15- och 6-sekundersversion redan i manus.",
  },
  {
    q: "Hur långt är ett reklamfilmsmanus?",
    a: "En 30-sekunders reklamfilm rymmer cirka 65–75 ord speakertext, en 15-sekunders cirka 35 ord. Manusdokumentet är oftast 1–2 sidor: scenlista, speaker, och en kolumn med produktionsnoteringar.",
  },
  {
    q: "Vad kostar ett reklamfilmsmanus?",
    a: "25 000–75 000 kr beroende på om koncept ingår, hur många idéspår som ska presenteras och hur många kanalversioner som ska skrivas fram. Enbart manus utifrån ett färdigt koncept ligger i nedre delen av spannet.",
  },
  {
    q: "Ska hooken ligga före eller efter varumärket?",
    a: "Före. Varumärket tidigt i bild ökar igenkänning men ökar också avhoppen i skippbara format. Praxis 2026 är hook i sekund 0–3, visuell varumärkesnärvaro genomgående och logotyp i slutet — utom i broadcast där logotypen ofta ligger både först och sist.",
  },
  {
    q: "Behöver varje kanal ett eget manus?",
    a: "Inte ett eget manus, men egna versioner. Vi skriver en master och sedan varianter: 9:16 med text-on-screen och ljudoberoende berättelse, 16:9 för YouTube och broadcast, och en 6-sekunders bumper som bara bär ett enda budskap.",
  },
];

const TITLE = "Reklamfilmsmanus — struktur, hook och pris | Fenny";
const DESCRIPTION =
  "Manus till reklamfilm: hookstruktur, ordantal per längd, versionering per kanal och vad ett reklamfilmsmanus kostar i Sverige 2026.";

export const Route = createFileRoute("/reklamfilmsmanus")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Reklamfilmsmanus — Fenny" },
      { property: "og:description", content: "Så byggs ett reklamfilmsmanus som håller i alla kanaler." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://fenny.se/reklamfilmsmanus" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/reklamfilmsmanus" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: "/reklamfilmsmanus", name: TITLE, description: DESCRIPTION })) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Reklamfilmsmanus",
            path: "/reklamfilmsmanus",
            serviceType: "Manus för reklamfilm",
            description: "Manus och koncept för reklamfilm, inklusive versionering för broadcast, YouTube och paid social.",
            priceRange: "25 000–75 000 kr",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Manus och koncept", path: "/manus-och-koncept" },
            { name: "Reklamfilmsmanus", path: "/reklamfilmsmanus" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Reklamfilmsmanus"
        title={<>Reklamfilmsmanus som håller i tre sekunder — och i tre kanaler.</>}
        lede="Ett reklamfilmsmanus har ett enda jobb: få någon som inte bad om filmen att stanna kvar. Vi skriver manus med hook i sekund noll, ett budskap som går att bevisa i bild, och versioner för broadcast, YouTube och paid social skrivna redan från start."
        tldr={
          <p>
            Ett reklamfilmsmanus byggs i fyra block: hook (0–3 sek), problem
            eller insikt, bevis eller demo, och avslut med varumärke och
            uppmaning. Ordantal: cirka <strong>65–75 ord för 30 sekunder</strong>{" "}
            och <strong>35 ord för 15 sekunder</strong>. Pris i Sverige 2026:{" "}
            <strong>25 000–75 000 kr</strong> beroende på om koncept och
            kanalversioner ingår.
          </p>
        }
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Reklamfilm", to: "/reklamfilm" }}
        trackingId="reklamfilmsmanus"
        bullets={[
          { title: "Hook först", body: "De tre första sekunderna skrivs och testas separat från resten av filmen." },
          { title: "Versioner i manus", body: "30, 15 och 6 sekunder samt 9:16 skrivs fram direkt — inte i efterhand." },
          { title: "Producerbart", body: "Varje scen prövas mot budget, inspelningsdagar och tillstånd innan den godkänns." },
        ]}
        pricing={{
          heading: "Vad kostar ett reklamfilmsmanus?",
          intro: "Riktpriser exklusive moms, Sverige 2026.",
          rows: [
            { format: "Manus på befintligt koncept", scope: "En film, en längd, 2 revideringar", price: "25 000–35 000 kr", lead: "1–2 veckor" },
            { format: "Koncept + manus", scope: "2–3 idéspår, valt koncept skrivs klart", price: "40 000–60 000 kr", lead: "2–3 veckor" },
            { format: "Kampanjmanus", scope: "Master + 30/15/6 sek + 9:16-varianter", price: "50 000–75 000 kr", lead: "3–4 veckor" },
            { format: "Storyboard till manuset", scope: "Bildmanus 20–40 rutor", price: "+18 000–35 000 kr", lead: "1–2 veckor" },
          ],
          footnote: "Manus och koncept ingår i offerten när vi producerar reklamfilmen som Prime-produktion.",
        }}
        sections={[
          {
            id: "struktur",
            heading: "Strukturen som fungerar",
            body: (
              <>
                <p>
                  <strong>Sekund 0–3: hook.</strong> En bild eller ett påstående
                  som bryter mönstret i flödet. Inget varumärkesintro, ingen
                  uppvärmning. <strong>Sekund 3–10: insikt.</strong> Situationen
                  tittaren känner igen sig i.
                </p>
                <p>
                  <strong>Sekund 10–22: bevis.</strong> Produkten eller tjänsten i
                  arbete — visad, inte påstådd.{" "}
                  <strong>Sekund 22–30: avslut.</strong> Varumärke, ett löfte och
                  en uppmaning. Testa manuset genom att läsa bara denna sista del
                  högt: förstår någon vad ni säljer?
                </p>
              </>
            ),
          },
          {
            id: "kanaler",
            heading: "En master, flera kanalversioner",
            body: (
              <p>
                Broadcast kräver exakt 15/20/30 sekunder och logotyp enligt
                kanalens regler. YouTube pre-roll kräver att budskapet landat före
                sekund fem. Paid social kräver att filmen fungerar utan ljud, med
                text-on-screen och 9:16-komposition. Vi skriver alla varianter
                samtidigt så att inspelningen täcker allt — det är billigare än
                att versionera i efterhand. Se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">reklamfilm</a>{" "}
                för produktionsdelen.
              </p>
            ),
          },
          {
            id: "vanliga-fel",
            heading: "Fyra fel som dödar ett reklamfilmsmanus",
            body: (
              <>
                <p>
                  <strong>1. För många budskap.</strong> En film, en sak.{" "}
                  <strong>2. Speaker som beskriver bilden.</strong> Låt bilden
                  göra sitt jobb och texten säga något annat.
                </p>
                <p>
                  <strong>3. Överlång text.</strong> Om speakern måste stressa
                  fram orden är manuset för långt — räkna om mot 130–150 ord per
                  minut. <strong>4. Avslut utan uppmaning.</strong> Tittaren ska
                  veta exakt vad nästa steg är.
                </p>
              </>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        riskReducers
        closing={{
          title: "Ska ni göra reklamfilm?",
          body: "Boka 30 minuter så skissar vi hook, struktur och kanalversioner tillsammans.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["manus", "reklamfilm", "kampanjfilm", "strategi"]} />
    </>
  );
}
